import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Buildings, CalendarBlank, CheckCircle, FilePdf, LockKey, PencilSimple, Sparkle, SpinnerGap, Sun, Trash, X } from "@phosphor-icons/react";
import { Header, MobileNav } from "@/components/Header";
import PhotoUploader from "@/components/PhotoUploader";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBuilding } from "@/context/BuildingContext";
import { formatDateShort } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { toast } from "@/components/ui/sonner";

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function Cleaning() {
  const BUILDING = useBuilding();
  const { user, can } = useAuth();
  const [visits, setVisits] = useState([]);
  const [name, setName] = useState("");
  const [visitDate, setVisitDate] = useState(todayISO());
  const [note, setNote] = useState("");
  const [subBuilding, setSubBuilding] = useState("");
  const [photos, setPhotos] = useState([]);
  const [sending, setSending] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [histFilter, setHistFilter] = useState("tous");
  const [leaves, setLeaves] = useState([]);
  const [leaveStart, setLeaveStart] = useState("");
  const [leaveEnd, setLeaveEnd] = useState("");
  const [leaveNote, setLeaveNote] = useState("");
  const [leaveSending, setLeaveSending] = useState(false);
  const [confirmDeleteLeave, setConfirmDeleteLeave] = useState(null);
  const [searchParams] = useSearchParams();
  const providerToken = searchParams.get("prestataire") || "";
  const [providerAccess, setProviderAccess] = useState(null);
  const canSubmit = can("visits") || providerAccess?.ok === true;
  const validatingToken = !!providerToken && providerAccess === null;
  const [exportFrom, setExportFrom] = useState("");
  const [exportTo, setExportTo] = useState("");
  const exportParams = [exportFrom && `du=${exportFrom}`, exportTo && `au=${exportTo}`].filter(Boolean).join("&");
  const exportUrl = `${process.env.REACT_APP_BACKEND_URL}/api/admin/cleaning-visits/export.pdf${exportParams ? `?${exportParams}` : ""}`;
  const filteredVisits = histFilter === "tous" ? visits : visits.filter((v) => v.sub_building === histFilter);

  useEffect(() => {
    api.get("/cleaning-visits").then((r) => setVisits(r.data)).catch(() => {});
    api.get("/cleaning-leaves").then((r) => setLeaves(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!providerToken) return;
    api.get(`/cleaning-access?token=${encodeURIComponent(providerToken)}`)
      .then((r) => {
        setProviderAccess(r.data);
        if (r.data?.building_id && r.data.building_id !== BUILDING.id) {
          api.get(`/cleaning-visits?b=${r.data.building_id}`).then((res) => setVisits(res.data)).catch(() => {});
          api.get(`/cleaning-leaves?b=${r.data.building_id}`).then((res) => setLeaves(res.data)).catch(() => {});
        }
      })
      .catch(() => setProviderAccess(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerToken]);

  const today = todayISO();
  const currentLeave = leaves.find((l) => l.start_date <= today && l.end_date >= today);
  const upcomingLeaves = leaves.filter((l) => l.start_date > today).sort((a, b) => a.start_date.localeCompare(b.start_date));

  const addLeave = async () => {
    if (!leaveStart || !leaveEnd) {
      toast.error("Indiquez le début et la fin des congés");
      return;
    }
    setLeaveSending(true);
    try {
      const { data } = await api.post("/cleaning-leaves", { start_date: leaveStart, end_date: leaveEnd, note: leaveNote || null });
      setLeaves((list) => [data, ...list]);
      setLeaveStart("");
      setLeaveEnd("");
      setLeaveNote("");
      toast.success("Congés enregistrés — la société de nettoyage assurera le relais");
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setLeaveSending(false);
  };

  const removeLeave = async (l) => {
    if (confirmDeleteLeave !== l.id) {
      setConfirmDeleteLeave(l.id);
      window.setTimeout(() => setConfirmDeleteLeave((c) => (c === l.id ? null : c)), 4000);
      return;
    }
    try {
      await api.delete(`/cleaning-leaves/${l.id}`);
      setLeaves((list) => list.filter((x) => x.id !== l.id));
      setConfirmDeleteLeave(null);
      toast.success("Période de congés supprimée");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => {
    if (user && user.name && !name && !editingId) setName(user.name);
  }, [user]);

  const submit = async () => {
    if (!name.trim()) {
      toast.error("Indiquez votre nom pour confirmer le passage");
      return;
    }
    if (!visitDate) {
      toast.error("Indiquez le jour du passage");
      return;
    }
    if ((BUILDING.sub_buildings || []).length > 0 && !subBuilding) {
      toast.error("Sélectionnez le bâtiment concerné par le passage");
      return;
    }
    setSending(true);
    const payload = { name, note: note || null, photos, visit_at: `${visitDate}T12:00:00`, sub_building: subBuilding || null };
    try {
      if (!can("visits") && providerAccess?.ok) {
        const { data } = await api.post("/cleaning-visits/public", { ...payload, token: providerToken });
        setVisits((v) =>
          [data, ...v].sort(
            (a, b) => new Date(b.visit_at || b.created_at) - new Date(a.visit_at || a.created_at)
          )
        );
        toast.success("Passage confirmé, merci !");
      } else if (editingId) {
        const { data } = await api.patch(`/cleaning-visits/${editingId}`, payload);
        setVisits((list) =>
          [...list.map((v) => (v.id === editingId ? data : v))].sort(
            (a, b) => new Date(b.visit_at || b.created_at) - new Date(a.visit_at || a.created_at)
          )
        );
        setEditingId(null);
        toast.success("Passage mis à jour");
      } else {
        const { data } = await api.post("/cleaning-visits", payload);
        setVisits((v) =>
          [data, ...v].sort(
            (a, b) => new Date(b.visit_at || b.created_at) - new Date(a.visit_at || a.created_at)
          )
        );
        toast.success("Passage confirmé, merci !");
      }
      setNote("");
      setSubBuilding("");
      setPhotos([]);
      setVisitDate(todayISO());
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setSending(false);
  };

  const startEdit = (v) => {
    setEditingId(v.id);
    setName(v.name);
    setVisitDate((v.visit_at || v.created_at).slice(0, 10));
    setNote(v.note || "");
    setSubBuilding(v.sub_building || "");
    setPhotos(v.photos || []);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setName(user?.name || "");
    setVisitDate(todayISO());
    setNote("");
    setSubBuilding("");
    setPhotos([]);
  };

  const removeVisit = async (v) => {
    if (confirmDelete !== v.id) {
      setConfirmDelete(v.id);
      window.setTimeout(() => setConfirmDelete((c) => (c === v.id ? null : c)), 4000);
      return;
    }
    try {
      await api.delete(`/cleaning-visits/${v.id}`);
      setVisits((list) => list.filter((x) => x.id !== v.id));
      setConfirmDelete(null);
      if (editingId === v.id) cancelEdit();
      toast.success("Passage supprimé de l'historique");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (BUILDING.features && BUILDING.features.cleaning === false) {
    return (
      <div className="min-h-screen pb-24 md:pb-0" data-testid="cleaning-disabled-page">
        <Header />
        <main className="mx-auto max-w-lg px-5 py-20 text-center">
          <Sparkle size={48} weight="duotone" className="mx-auto text-muted-foreground" />
          <h1 className="mt-4 font-display text-3xl font-extrabold">Suivi entretien désactivé</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Le gestionnaire a désactivé le suivi d'entretien pour cette copropriété.
          </p>
          <Link to="/" className="mt-6 inline-block rounded-full bg-primary px-6 py-3 text-sm font-bold text-primary-foreground">
            Retour à l'accueil
          </Link>
        </main>
        <MobileNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24 md:pb-0" data-testid="cleaning-page">
      <Header />
      <main className="mx-auto max-w-5xl px-5 py-10 sm:py-14">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">{BUILDING.name}</p>
          <h1 className="mt-2 font-display text-4xl font-extrabold tracking-[-0.02em] sm:text-5xl">Suivi entretien</h1>
          <p className="mt-3 max-w-lg text-sm text-muted-foreground">
            L'historique des passages de nettoyage des parties communes, en toute transparence.
          </p>
        </motion.div>

        {currentLeave && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="mt-6 flex items-start gap-3 rounded-3xl border border-[#E8C468]/40 bg-[#FBF3DF] px-5 py-4"
            data-testid="cleaning-leave-banner"
          >
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#E8C468]/30 text-[#8A6D1A]">
              <Sun size={20} weight="duotone" />
            </span>
            <div>
              <p className="text-sm font-bold text-[#6B5410]">
                Congés du concierge du {formatDateShort(currentLeave.start_date)} au {formatDateShort(currentLeave.end_date)}
              </p>
              <p className="mt-0.5 text-xs text-[#8A6D1A]">
                La société de nettoyage assure le relais pendant cette période et note ses passages ci-dessous.
              </p>
              {currentLeave.note && <p className="mt-1 text-xs text-[#8A6D1A]/80">{currentLeave.note}</p>}
            </div>
          </motion.div>
        )}
        {!currentLeave && upcomingLeaves.length > 0 && (
          <p className="mt-6 text-xs text-muted-foreground" data-testid="cleaning-leave-upcoming">
            Prochains congés du concierge : du {formatDateShort(upcomingLeaves[0].start_date)} au {formatDateShort(upcomingLeaves[0].end_date)} — la société de nettoyage assurera le relais.
          </p>
        )}

        <div className="mt-10 grid gap-8 lg:grid-cols-[1fr_1.2fr]">
          <div>
            {(user === null || validatingToken) && !canSubmit && (
              <div className="h-64 animate-pulse rounded-3xl bg-black/5" data-testid="cleaning-form-loading" />
            )}

            {user !== null && !canSubmit && !validatingToken && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="rounded-3xl border border-black/5 bg-white p-8 text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
                data-testid="cleaning-locked-card"
              >
                <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                  <LockKey size={26} weight="duotone" />
                </span>
                <h2 className="mt-4 font-display text-lg font-bold tracking-tight">Espace réservé</h2>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground" data-testid="cleaning-locked-message">
                  {providerAccess === false
                    ? "Ce lien de confirmation est invalide ou n'est plus actif. Demandez un nouveau lien au gestionnaire de la copropriété."
                    : user === false
                    ? "Seuls le gestionnaire, le concierge et l'agent d'entretien autorisés peuvent noter un passage. L'historique reste consultable par tous les résidents."
                    : "Votre compte n'a pas le droit « Entretien ». Demandez au gestionnaire de l'activer dans l'onglet Accès."}
                </p>
                {user === false && (
                  <Link
                    to="/admin/login"
                    data-testid="cleaning-admin-login-link"
                    className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-bold text-primary-foreground transition-colors hover:bg-[#265442]"
                  >
                    <LockKey size={16} weight="bold" />
                    Connexion
                  </Link>
                )}
              </motion.div>
            )}

            {canSubmit && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="h-fit rounded-3xl border border-black/5 bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] lg:sticky lg:top-24"
                data-testid="cleaning-form"
              >
                {providerAccess?.ok && !can("visits") && (
                  <p className="mb-3 rounded-xl bg-primary/10 px-3 py-2 text-[11px] font-bold text-primary" data-testid="cleaning-provider-banner">
                    Lien prestataire sécurisé — {providerAccess.building_name}
                  </p>
                )}
                <h2 className="font-display text-lg font-bold tracking-tight">
                  {editingId ? "Compléter ce passage" : "Noter un passage d'entretien"}
                </h2>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Votre nom (ex. Karim B.)"
                  data-testid="cleaning-name-input"
                  className="mt-5 w-full rounded-2xl border border-black/10 px-5 py-3.5 text-sm outline-none transition-colors focus:border-primary"
                />
                <div className="relative mt-3">
                  <CalendarBlank size={18} weight="duotone" className="pointer-events-none absolute left-5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="date"
                    value={visitDate}
                    onChange={(e) => setVisitDate(e.target.value)}
                    data-testid="cleaning-date-input"
                    aria-label="Jour du passage"
                    className="w-full rounded-2xl border border-black/10 py-3.5 pl-12 pr-5 text-sm outline-none transition-colors focus:border-primary"
                  />
                </div>
                {(BUILDING.sub_buildings || []).length > 0 && (
                  <div className="mt-3">
                    <label htmlFor="cleaning-building" className="mb-1.5 block text-xs font-bold text-muted-foreground">
                      Bâtiment concerné <span className="text-[#C90A0A]">*</span>
                    </label>
                    <select
                      id="cleaning-building"
                      value={subBuilding}
                      onChange={(e) => setSubBuilding(e.target.value)}
                      required
                      data-testid="cleaning-building-select"
                      aria-label="Bâtiment concerné (obligatoire)"
                      className="w-full rounded-2xl border border-black/10 px-5 py-3.5 text-sm outline-none transition-colors focus:border-primary"
                    >
                      <option value="" disabled>Sélectionnez le bâtiment…</option>
                      {BUILDING.sub_buildings.map((s) => (
                        <option key={s.name} value={s.name}>{s.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={3}
                  placeholder="Note (optionnel) — une remarque, un point à signaler…"
                  data-testid="cleaning-note-input"
                  className="mt-3 w-full resize-none rounded-2xl border border-black/10 px-5 py-3.5 text-sm outline-none transition-colors focus:border-primary"
                />
                <div className="mt-5">
                  <PhotoUploader photos={photos} onChange={setPhotos} testidPrefix="cleaning-photo" />
                </div>
                <div className="mt-6 flex gap-2">
                  <button
                    type="button"
                    onClick={submit}
                    disabled={sending}
                    data-testid="cleaning-submit-button"
                    className="flex flex-1 items-center justify-center gap-2 rounded-full bg-primary px-7 py-4 text-sm font-bold text-primary-foreground shadow-[0_10px_28px_rgba(24,56,43,0.28)] transition-colors duration-300 hover:bg-[#265442] disabled:opacity-60"
                  >
                    {sending ? <SpinnerGap size={18} className="animate-spin" /> : editingId ? <PencilSimple size={18} weight="bold" /> : <Sparkle size={18} weight="fill" />}
                    {editingId ? "Enregistrer les modifications" : "Confirmer le passage de nettoyage"}
                  </button>
                  {editingId && (
                    <button
                      type="button"
                      onClick={cancelEdit}
                      data-testid="cleaning-cancel-edit"
                      className="inline-flex items-center gap-1.5 rounded-full border border-black/10 px-5 py-4 text-sm font-bold transition-colors hover:border-primary hover:text-primary"
                    >
                      <X size={15} weight="bold" /> Annuler
                    </button>
                  )}
                </div>
              </motion.div>
            )}

            {can("visits") && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.15 }}
                className="mt-6 rounded-3xl border border-black/5 bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
                data-testid="cleaning-leaves-card"
              >
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#E8C468]/25 text-[#8A6D1A]">
                    <Sun size={18} weight="duotone" />
                  </span>
                  <div>
                    <h2 className="font-display text-lg font-bold tracking-tight">Congés du concierge</h2>
                    <p className="text-[11px] text-muted-foreground">Pendant ces périodes, la société de nettoyage prend le relais et note ses passages.</p>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-2">
                  <label>
                    <span className="mb-1 block text-[11px] font-bold text-muted-foreground">Du</span>
                    <input
                      type="date"
                      value={leaveStart}
                      onChange={(e) => setLeaveStart(e.target.value)}
                      data-testid="cleaning-leave-start"
                      aria-label="Début des congés"
                      className="w-full rounded-xl border border-black/10 px-3 py-2.5 text-xs font-bold outline-none focus:border-primary"
                    />
                  </label>
                  <label>
                    <span className="mb-1 block text-[11px] font-bold text-muted-foreground">Au</span>
                    <input
                      type="date"
                      value={leaveEnd}
                      onChange={(e) => setLeaveEnd(e.target.value)}
                      data-testid="cleaning-leave-end"
                      aria-label="Fin des congés"
                      className="w-full rounded-xl border border-black/10 px-3 py-2.5 text-xs font-bold outline-none focus:border-primary"
                    />
                  </label>
                </div>
                <input
                  type="text"
                  value={leaveNote}
                  onChange={(e) => setLeaveNote(e.target.value)}
                  placeholder="Note (optionnel) — ex. « Joignable en urgence »"
                  data-testid="cleaning-leave-note"
                  className="mt-2 w-full rounded-xl border border-black/10 px-4 py-2.5 text-xs outline-none transition-colors focus:border-primary"
                />
                <button
                  type="button"
                  onClick={addLeave}
                  disabled={leaveSending}
                  data-testid="cleaning-leave-add"
                  className="mt-3 flex w-full items-center justify-center gap-2 rounded-full border-2 border-primary px-5 py-3 text-xs font-bold text-primary transition-colors hover:bg-primary hover:text-primary-foreground disabled:opacity-60"
                >
                  {leaveSending ? <SpinnerGap size={16} className="animate-spin" /> : <Sun size={16} weight="duotone" />}
                  Ajouter cette période de congés
                </button>
                {leaves.length > 0 && (
                  <div className="mt-4 space-y-2" data-testid="cleaning-leaves-list">
                    {leaves.map((l, li) => (
                      <div key={l.id} className="flex items-center justify-between gap-2 rounded-xl border border-black/5 bg-black/[0.02] px-3 py-2" data-testid={`leave-entry-${li}`}>
                        <div className="min-w-0">
                          <p className="text-xs font-bold">
                            Du {formatDateShort(l.start_date)} au {formatDateShort(l.end_date)}
                          </p>
                          <p className="truncate text-[11px] text-muted-foreground">
                            {l.note || "Relais société de nettoyage"}{l.created_by ? ` · par ${l.created_by}` : ""}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeLeave(l)}
                          data-testid={`leave-delete-${li}`}
                          className={cn(
                            "shrink-0 rounded-full border px-3 py-1.5 text-[11px] font-bold transition-all duration-200",
                            confirmDeleteLeave === l.id
                              ? "border-[#C90A0A] bg-[#C90A0A] text-white"
                              : "border-black/10 text-foreground/50 hover:border-[#C90A0A] hover:text-[#C90A0A]"
                          )}
                        >
                          {confirmDeleteLeave === l.id ? "Confirmer ?" : "Supprimer"}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            )}
          </div>

          <div>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="font-display text-lg font-bold tracking-tight">Historique des passages</h2>
            </div>
            {user && can("visits") && (
              <div className="mt-3 rounded-2xl border border-black/5 bg-white p-4" data-testid="visits-export-block">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <FilePdf size={18} weight="duotone" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold">Export pour assemblée générale</p>
                    <p className="text-xs text-muted-foreground">Filtrez par période (optionnel)</p>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap items-end gap-2">
                  <label className="min-w-0 flex-1 basis-32">
                    <span className="mb-1 block text-[11px] font-bold text-muted-foreground">Du</span>
                    <input
                      type="date"
                      value={exportFrom}
                      onChange={(e) => setExportFrom(e.target.value)}
                      data-testid="visits-export-from"
                      className="w-full rounded-xl border border-black/10 px-3 py-2 text-xs font-bold outline-none focus:border-primary"
                    />
                  </label>
                  <label className="min-w-0 flex-1 basis-32">
                    <span className="mb-1 block text-[11px] font-bold text-muted-foreground">Au</span>
                    <input
                      type="date"
                      value={exportTo}
                      onChange={(e) => setExportTo(e.target.value)}
                      data-testid="visits-export-to"
                      className="w-full rounded-xl border border-black/10 px-3 py-2 text-xs font-bold outline-none focus:border-primary"
                    />
                  </label>
                  <a
                    href={exportUrl}
                    data-testid="visits-export-pdf"
                    className="inline-flex w-full items-center justify-center gap-1.5 rounded-xl bg-primary px-5 py-2.5 text-xs font-bold text-primary-foreground transition-colors hover:bg-[#265442] sm:w-auto"
                  >
                    <FilePdf size={14} weight="duotone" /> Télécharger le PDF
                  </a>
                </div>
              </div>
            )}
            {(BUILDING.sub_buildings || []).length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2" data-testid="visits-building-filter">
                <button
                  onClick={() => setHistFilter("tous")}
                  data-testid="visit-filter-tous"
                  className={cn(
                    "rounded-full border px-4 py-2 text-xs font-bold transition-all duration-200",
                    histFilter === "tous"
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-black/10 bg-white text-foreground/60 hover:border-primary/40"
                  )}
                >
                  Tous
                </button>
                {BUILDING.sub_buildings.map((s, bi) => (
                  <button
                    key={s.name}
                    onClick={() => setHistFilter(histFilter === s.name ? "tous" : s.name)}
                    data-testid={`visit-filter-${bi}`}
                    className={cn(
                      "rounded-full border px-4 py-2 text-xs font-bold transition-all duration-200",
                      histFilter === s.name
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-black/10 bg-white text-foreground/60 hover:border-primary/40"
                    )}
                  >
                    {s.name}
                  </button>
                ))}
              </div>
            )}
            <div className="mt-5 space-y-4" data-testid="visits-history">
              {visits.length === 0 && (
                <p className="rounded-3xl border border-black/5 bg-white p-8 text-center text-sm text-muted-foreground" data-testid="visits-empty">
                  Aucun passage enregistré pour le moment.
                </p>
              )}
              {visits.length > 0 && filteredVisits.length === 0 && (
                <p className="rounded-3xl border border-black/5 bg-white p-8 text-center text-sm text-muted-foreground" data-testid="visits-empty-filter">
                  Aucun passage enregistré pour ce bâtiment.
                </p>
              )}
              {filteredVisits.map((v, i) => (
                <motion.div
                  key={v.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: Math.min(i * 0.06, 0.4) }}
                  className={cn(
                    "rounded-3xl border bg-white p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]",
                    editingId === v.id ? "border-primary ring-1 ring-primary/20" : "border-black/5"
                  )}
                  data-testid={`visit-entry-${i}`}
                >
                  <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-display text-base font-bold">{formatDateShort(v.visit_at || v.created_at)}</p>
                        {v.sub_building && (
                          <span
                            data-testid={`visit-building-${i}`}
                            className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-0.5 text-[11px] font-bold text-primary"
                          >
                            <Buildings size={12} weight="duotone" />
                            {v.sub_building}
                          </span>
                        )}
                      </div>
                      <p className="mt-0.5 text-xs text-muted-foreground">{v.name}</p>
                    </div>
                    <div className="flex shrink-0 flex-wrap items-center gap-2">
                      {v.source === "concierge" && (
                        <span data-testid={`visit-source-${i}`} className="inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-xs font-bold text-primary">
                          Concierge
                        </span>
                      )}
                      {v.source === "societe" && (
                        <span data-testid={`visit-source-${i}`} className="inline-flex items-center rounded-full bg-[#E8F0FE] px-3 py-1 text-xs font-bold text-[#1A56DB]">
                          Société de nettoyage
                        </span>
                      )}
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-[#E2F6ED] px-3 py-1 text-xs font-bold text-[#007A4D]">
                        <CheckCircle size={14} weight="fill" /> OK
                      </span>
                      {can("visits") && (
                        <>
                          <button
                            onClick={() => startEdit(v)}
                            data-testid={`visit-edit-${i}`}
                            aria-label="Compléter ce passage"
                            className="flex h-9 w-9 items-center justify-center rounded-full border border-black/10 text-foreground/50 transition-colors hover:border-primary hover:text-primary"
                          >
                            <PencilSimple size={15} weight="duotone" />
                          </button>
                          <button
                            onClick={() => removeVisit(v)}
                            data-testid={`visit-delete-${i}`}
                            className={cn(
                              "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-bold transition-all duration-200",
                              confirmDelete === v.id
                                ? "border-[#C90A0A] bg-[#C90A0A] text-white"
                                : "border-black/10 text-foreground/50 hover:border-[#C90A0A] hover:text-[#C90A0A]"
                            )}
                          >
                            <Trash size={13} weight="duotone" />
                            {confirmDelete === v.id ? "Confirmer ?" : "Supprimer"}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  {v.note && <p className="mt-3 text-sm leading-relaxed text-foreground/75">{v.note}</p>}
                  {v.photos?.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {v.photos.map((p, j) => (
                        <a key={p} href={fileUrl(p)} target="_blank" rel="noreferrer" data-testid={`visit-photo-${i}-${j}`}>
                          <img src={fileUrl(p)} alt={`Photo ${j + 1}`} className="h-16 w-16 rounded-xl border border-black/5 object-cover transition-transform duration-200 hover:scale-105" />
                        </a>
                      ))}
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </main>
      <MobileNav />
    </div>
  );
}
